import { Box, Fade, Icon, Tooltip, Typography, useTheme, Zoom } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { ColorTokens, Palette } from 'src/feature/theme/model/Palette'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppUnstyledButton } from './button/AppUnstyledButton'
import StarsRoundedIcon from '@mui/icons-material/StarsRounded'
import { AppBox } from './AppBox'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import { getCardRatingColor } from 'src/feature/my-team/util/skillGapUtils'
import { Word } from 'src/feature/localization/component/atom/Word'
import { iconMapping } from 'src/util/icon-mapping'

export type CircularRatingProps = {
  size?: number
  totalSegments?: number
  strokeWidth?: number
  paddingAngle?: number
  indexKey: number
  userRating?: number
  expectedRating?: number
  label: string
  showLevelBtn?: boolean
  onRateSkillClick?: (index: number) => void
  showRemoveBtn?: boolean
  onRemoveSkillClick?: () => void
  showRating?: boolean
  viewLabel?: string
  defaultColor?: string
  isValidated?: boolean
}

export const AppCircularRating = observer(({
  size = 160,
  strokeWidth = 7,
  paddingAngle = 10,
  totalSegments = 5,
  indexKey,
  userRating,
  expectedRating,
  label,
  showLevelBtn = false,
  onRateSkillClick,
  showRemoveBtn = false,
  onRemoveSkillClick,
  showRating = false,
  viewLabel,
  defaultColor,
  isValidated = true,
}: CircularRatingProps) => {
  const theme = useTheme()
  const { translation } = useLocalizationStore()

  const radius = size / 2 - strokeWidth / 2
  const segmentAngle = 360 / totalSegments
  const gapAngle = paddingAngle
  const arcAngle = segmentAngle - gapAngle
  const center = size / 2

  const polarToCartesian = (cx: number, cy: number, radius: number, angle: number) => {
    const rad = (angle * Math.PI) / 180
    
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad),
    }
  }

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle)
    const end = polarToCartesian(x, y, radius, startAngle)

    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

    return [
      `M ${start.x} ${start.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    ].join(' ')
  }
  
  const getSegmentColor = (segmentIndex: number) => {
    if (viewLabel === 'UserView') {
      if (showRating && userRating && (segmentIndex < userRating)) {
        return `${getCardRatingColor(userRating)}`
      }
    }

    if (viewLabel === 'ManagerView') {
      if (expectedRating && (segmentIndex < expectedRating)) {
        return `${getCardRatingColor(expectedRating)}`
      }
    }
  }

  return (
    <Box
      sx={styles.root}
      onClick={() => !showRemoveBtn && showLevelBtn && onRateSkillClick && onRateSkillClick(indexKey)}
    >
      <Tooltip title={label} placement='top' arrow>
        <Typography
          sx={{
            ...styles.label,
            width: size,
          }}
          noWrap
        >
          {label}
        </Typography>
      </Tooltip>

      <Box sx={{ ...styles.ringWrapper, width: size, height: size }}>
        <Zoom in={true} timeout={700} mountOnEnter unmountOnExit>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* Segments */}
            {[...Array(totalSegments)].map((_, index) => {
              const startAngle = index * segmentAngle - 90
              const endAngle = startAngle + arcAngle
              const segmentColor = getSegmentColor(index) || (defaultColor || theme.palette.background.default)

              return (
                <path
                  key={`segment-${startAngle}`}
                  d={describeArc(center, center, radius, startAngle, endAngle)}
                  fill="none"
                  stroke={segmentColor}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                />
              )
            })}
          </svg>
        </Zoom>

        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#F9F2D1',
            borderRadius: '999px',
            height: 100,
            width: 100,
          }}
        >
          <Fade in={viewLabel === 'UserView'} timeout={800} mountOnEnter unmountOnExit>
            <Typography>
              <Word sx={styles.ratingWrapper}>{userRating || '0'}</Word>
              {isValidated && <Word>{` /${expectedRating || '0'}`}</Word>}
            </Typography>
          </Fade>
          <Fade in={viewLabel === 'ManagerView'} timeout={800} mountOnEnter unmountOnExit>
            <Typography>
              <Word sx={styles.ratingWrapper}>{expectedRating || '0'}</Word>
              <Word>{` /${'5'}`}</Word>
            </Typography>
          </Fade>
          <AppBox alignItems='center' justifyContent='center' gap={1}>
            {(userRating !== null && expectedRating !== 0) && isValidated && (
              <Fade in={showRating} timeout={800} mountOnEnter unmountOnExit>
                <Tooltip title={translation.pages.myTeam.sortHeader.validated()}>
                  <VerifiedRoundedIcon fontSize='small' sx={{ color: Palette.GOLDEN_ORANGE }} />
                </Tooltip>
              </Fade>
            )}
            {(userRating !== null && expectedRating !== 0) && isValidated && ((userRating ?? 0) > (expectedRating ?? 0)) && (
              <Fade in={showRating} timeout={800} mountOnEnter unmountOnExit>
                <StarsRoundedIcon fontSize='small' sx={{ color: Palette.GOLDEN_ORANGE }} />
              </Fade>
            )}
          </AppBox>
          {showRemoveBtn && showLevelBtn && (
            <AppUnstyledButton sx={styles.closeButton} type={'button'} onClick={onRemoveSkillClick}>
              <Tooltip title={translation.pages.editPositions.removeButton()}>
                <Icon sx={styles.helpIcon} component={iconMapping['DeleteForeverRoundedIcon']} />
              </Tooltip>
            </AppUnstyledButton>
          )}
        </Box>
      </Box>
    </Box>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    width: 'fit-content',
  },
  label: {
    textAlign: 'center',
    fontSize: 12,
  },
  ringWrapper: {
    position: 'relative',
    display: 'inline-block',
    transition: 'cursor 0.3s ease, transform 0.3s ease',
    '&:hover': {
      cursor: 'pointer',
      transform: 'scale(1.03)',
    },
  },
  ratingWrapper: {
    fontSize: 24,
    fontWeight: 600,
  },
  closeButton: {
    borderRadius: '50%',
    backgroundColor: ColorTokens.red10,
    width: 30,
    height: 30,
  },
  helpIcon: {
    height: 20,
    width: 20,
    color: ColorTokens.red200,
    cursor: 'help',
    flexShrink: 0,
  },
}
